import { useQuery } from '@tanstack/react-query'
import api from '../api'
import { type CompanyFilter, companyKey, serializeCompanyId } from './metrics.service'

// Types
export interface WorkbookOverview {
  totalViews: number
  totalDownloads: number
  totalActions: number
  uniqueUsers: number
  growth: number
  avgPerDay: number
  conversionRate: number
  recentActions: Array<{
    id: string
    action: 'view' | 'download'
    createdAt: string
    users: {
      id: string
      name: string
      email: string
    }
    episodes: {
      id: number
      title: string
      moduleId: number
    }
  }>
}

export interface WorkbookRankingEpisode {
  episodeId: number
  episode: {
    id: number
    title: string
    moduleId: number
    moduleName: string
  } | null
  totalActions: number
  views: number
  downloads: number
}

export interface WorkbookRankingUser {
  userId: string
  user: {
    id: string
    name: string
    email: string
    avatar?: string
  } | null
  totalActions: number
  views: number
  downloads: number
}

export interface WorkbookTimelineItem {
  date: string
  views: number
  downloads: number
  total: number
}

// Hooks
export function useWorkbookOverview(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ['analytics', 'workbooks', 'overview', dateFrom, dateTo],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (dateFrom) params.dateFrom = dateFrom
      if (dateTo) params.dateTo = dateTo

      const response = await api.get<WorkbookOverview>('/analytics/workbooks/overview', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function useWorkbookRankings(
  type: 'episodes' | 'users' | 'courses' = 'episodes',
  period: '7d' | '30d' | '90d' | 'all' = '30d',
  limit: number = 10,
  companyId?: CompanyFilter
) {
  return useQuery({
    queryKey: ['analytics', 'workbooks', 'rankings', type, period, limit, companyKey(companyId)],
    queryFn: async () => {
      const params: Record<string, string | number> = { type, period, limit }
      const company = serializeCompanyId(companyId)
      if (company) params.companyId = company
      const response = await api.get<WorkbookRankingEpisode[] | WorkbookRankingUser[]>(
        '/analytics/workbooks/rankings',
        { params }
      )
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useWorkbookTimeline(
  dateFrom: string,
  dateTo: string,
  groupBy: 'day' | 'week' | 'month' = 'day',
  courseId?: number,
  moduleId?: number
) {
  return useQuery({
    queryKey: ['analytics', 'workbooks', 'timeline', dateFrom, dateTo, groupBy, courseId, moduleId],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        dateFrom,
        dateTo,
        groupBy,
      }
      if (courseId) params.courseId = courseId
      if (moduleId) params.moduleId = moduleId

      const response = await api.get<WorkbookTimelineItem[]>('/analytics/workbooks/timeline', {
        params,
      })
      return response.data
    },
    enabled: !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  })
}
